import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createFileRoute, Outlet, useChildMatches, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  Copy,
  Loader2,
  Lock,
  MoreVertical,
  Percent,
  Pencil,
  RefreshCw,
  Shield,
  Star,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { usePricebookItems } from '@/hooks/pricebook/usePricebookItems';
import { formatSeasonalWindow } from '@/lib/membership/seasonal-window';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { isComplimentaryBillingOption } from '@/lib/membership/complimentary-option';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { DetailPageLayout, useWorkspaceHeader } from '@/components/layouts';
import {
  CollapsibleSectionHeader,
  DetailCard,
  DetailField,
  DetailFieldGrid,
  OverflowMenuContent,
} from '@/components/molecules';
import { StatusChip } from '@/components/molecules/StatusChip';
import { membershipPlanKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { extraUnitPriceOf } from '@/lib/membership/extra-unit-price';
import {
  usePlanRecurringServices,
  type PlanRecurringService,
} from '@/hooks/membership-plans/usePlanRecurringServices';
import { buildDuplicateMembershipPlanPayload } from '@/lib/membership/duplicate-plan';
import {
  BILLING_FREQUENCY_PERIOD,
  COVERED_SERVICE_PERIOD_LABELS,
  WAIVER_TYPE_LABELS,
  formatBillingInterval,
  formatMembershipPlanDiscount,
  membershipPlanApplicableTypes,
  membershipPlanBillingAmount,
  membershipPlanBillingFrequency,
  membershipPlanHasCustomRates,
  legacyVisitPromises,
  planId,
  type MembershipPlan,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import {
  MembershipPlanVisitScheduleNotice,
  type MembersWithoutVisitSchedule,
} from '@/components/organisms/pricebook/MembershipPlanVisitScheduleNotice';

export const Route = createFileRoute('/_dashboard/dashboard/membership-plans/$id')({
  component: MembershipPlanDetailPage,
});

type PlanStats = {
  activeMemberCount: number;
  monthlyRecurring: number;
  membersWithoutVisitSchedule?: MembersWithoutVisitSchedule;
};

function MembershipPlanDetailPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const childMatches = useChildMatches();
  const [showSkeleton, setShowSkeleton] = useState(false);

  const { data, isLoading } = useApiQuery<{ data: MembershipPlan } | MembershipPlan>({
    queryKey: membershipPlanKeys.detail(id),
    queryFn: () =>
      apiGet<{ data: MembershipPlan } | MembershipPlan>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlan(id),
      ),
  });

  const plan: MembershipPlan | undefined =
    data && 'data' in data ? (data as { data: MembershipPlan }).data : (data as MembershipPlan);

  const { data: statsData } = useApiQuery<{ data: PlanStats } | PlanStats>({
    queryKey: membershipPlanKeys.detailStats(id),
    queryFn: () =>
      apiGet<{ data: PlanStats } | PlanStats>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlanStats(id),
      ),
    enabled: Boolean(plan),
    showErrorToast: false,
    // The office fixes the members this names on their membership pages; the
    // count has to be current when they come back.
    staleTime: 0,
  });

  const stats: PlanStats | undefined =
    statsData && 'data' in statsData ? (statsData as { data: PlanStats }).data : statsData;
  const {
    data: recurringServices = [],
    isLoading: recurringServicesLoading,
    isSuccess: recurringServicesLoaded,
  } = usePlanRecurringServices(id);

  const duplicateMutation = useApiMutation<{ data: MembershipPlan } | MembershipPlan, void>({
    mutationFn: async () => {
      const result = await apiPost<{ data: MembershipPlan } | MembershipPlan>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlans(),
        buildDuplicateMembershipPlanPayload(plan, recurringServices),
      );
      return result;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: membershipPlanKeys.all(),
      });
      const created = result && 'data' in result ? result.data : result;
      const createdId = created?.id ?? '';
      if (createdId) {
        void router.navigate({
          to: '/dashboard/membership-plans/$id/edit',
          params: { id: createdId },
          search: (prev) => prev as never,
        });
      }
    },
  });

  const archiveMutation = useApiMutation<{ data: MembershipPlan } | MembershipPlan, void>({
    mutationFn: () =>
      apiDelete<{ data: MembershipPlan } | MembershipPlan>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlan(id),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: membershipPlanKeys.all(),
      });
      toast.success('Membership plan archived');
      void router.navigate({
        to: '/dashboard/membership-plans',
        search: (prev) => prev as never,
      });
    },
  });

  const restoreMutation = useApiMutation<{ data: MembershipPlan } | MembershipPlan, void>({
    mutationFn: () =>
      apiPost<{ data: MembershipPlan } | MembershipPlan>(
        operationsClient,
        operationsPricebookEndpoints.restoreMembershipPlan(id),
        {},
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: membershipPlanKeys.all(),
      });
      toast.success('Membership plan restored');
    },
  });
  const syncStripeMutation = useApiMutation<{ data: MembershipPlan } | MembershipPlan, void>({
    mutationFn: () =>
      apiPost<{ data: MembershipPlan } | MembershipPlan>(
        operationsClient,
        operationsPricebookEndpoints.syncMembershipPlanStripe(id),
        {},
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: membershipPlanKeys.detail(id),
      });
      toast.success('Membership plan synced with Stripe');
    },
  });

  useEffect(() => {
    if (!isLoading) return;
    const timeout = setTimeout(() => setShowSkeleton(true), 150);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  if (childMatches.length > 0) {
    return <Outlet />;
  }

  if (isLoading) {
    if (!showSkeleton) return null;
    return (
      <div className="px-5 py-4 space-y-4 animate-in fade-in duration-200">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-44 w-full rounded-xl" />
        <Skeleton className="h-44 w-full rounded-xl" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
        <p className="text-sm">Plan not found</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            void router.navigate({
              to: '/dashboard/membership-plans',
              search: (prev) => prev as never,
            })
          }
        >
          Back
        </Button>
      </div>
    );
  }

  const unscheduledVisits =
    // Only a list that loaded can say the plan has no schedule; a failed
    // request must not raise the warning or block Duplicate.
    recurringServicesLoaded && recurringServices.length === 0
      ? legacyVisitPromises(plan.coveredServices)
      : [];

  return (
    <DetailPageLayout key={id}>
      <MembershipPlanContextHeaderSlot plan={plan} />
      <PlanSummaryCard
        plan={plan}
        stats={stats}
        onBack={() =>
          void router.navigate({
            to: '/dashboard/membership-plans',
            search: (prev) => prev as never,
          })
        }
        actions={
          <MembershipPlanSummaryActions
            plan={plan}
            memberCount={stats?.activeMemberCount}
            onDuplicate={() => {
              // A copy would promise the same visits with nothing to schedule
              // them, which Operations refuses to save; say so up front.
              if (unscheduledVisits.length > 0) {
                toast.error(
                  'Add a visit schedule to this plan before duplicating it — the copy would promise visits nothing schedules.',
                );
                return;
              }
              duplicateMutation.mutate();
            }}
            onArchive={() => archiveMutation.mutate()}
            onRestore={() => restoreMutation.mutate()}
            isDuplicating={duplicateMutation.isPending || recurringServicesLoading}
            isArchiving={archiveMutation.isPending}
            isRestoring={restoreMutation.isPending}
          />
        }
      />

      <MembershipPlanVisitScheduleNotice
        unscheduledVisits={unscheduledVisits}
        members={stats?.membersWithoutVisitSchedule}
        onEditPlan={
          plan.status === 'ARCHIVED'
            ? undefined
            : () =>
                void router.navigate({
                  to: '/dashboard/membership-plans/$id/edit',
                  params: { id },
                  search: (prev) => prev as never,
                })
        }
      />

      {/* Two flowing columns so a bare plan (no benefits/services) stacks its
          cards without leaving the right column empty next to Contract & renewal. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-4">
          <DetailCard
            label="Member pricing"
            icon={Percent}
            description={
              membershipPlanHasCustomRates(plan)
                ? 'Custom rates — first match wins, top-down:'
                : undefined
            }
          >
            <MemberPricingContent plan={plan} />
          </DetailCard>

          <DetailCard label="Contract & renewal" icon={RefreshCw}>
            <ContractRenewalContent plan={plan} />
          </DetailCard>
        </div>

        <div className="flex flex-col gap-4">
          <DetailCard label="What’s included" icon={Shield}>
            <IncludedContent plan={plan} recurringServices={recurringServices} />
          </DetailCard>

          {plan.display?.valuePoints?.length ? (
            <DetailCard label="Benefits" icon={Star}>
              <BenefitsContent valuePoints={plan.display.valuePoints} />
            </DetailCard>
          ) : null}

          <InternalCard
            plan={plan}
            onSyncStripe={() => syncStripeMutation.mutate()}
            syncingStripe={syncStripeMutation.isPending}
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {plan.priceCurrency?.toUpperCase() ?? 'USD'} · Billed{' '}
        {formatBillingInterval(plan.billingIntervalValue, plan.billingIntervalUnit)}
        {plan.providerPriceId ? ' via Stripe' : ''}
      </p>
    </DetailPageLayout>
  );
}

function MembershipPlanContextHeaderSlot({ plan }: { plan: MembershipPlan }) {
  const { name, status, createdAt, updatedAt } = plan;
  const companyTimezone = useCompanyTimezone();

  const contextHeader = useMemo(() => {
    const created = formatDateTimeInTimeZone(createdAt, companyTimezone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const updated = formatDateTimeInTimeZone(updatedAt, companyTimezone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return (
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="truncate text-base font-semibold">{name}</h2>
          <StatusChip status={status} className="shrink-0" />
        </div>
        <p className="truncate text-xs text-muted-foreground">
          Created {created} · Updated {updated}
        </p>
      </div>
    );
  }, [companyTimezone, createdAt, name, status, updatedAt]);

  useWorkspaceHeader({ contextHeader }, [contextHeader]);
  return null;
}

function PlanSummaryCard({
  plan,
  stats,
  actions,
  onBack,
}: {
  plan: MembershipPlan;
  stats?: PlanStats;
  actions: ReactNode;
  onBack: () => void;
}) {
  const billingAmount = membershipPlanBillingAmount(plan);
  const frequency = membershipPlanBillingFrequency(plan);
  const freeOptions = (plan.billingOptions ?? []).filter(isComplimentaryBillingOption);
  const activeMembers = stats?.activeMemberCount;
  const monthlyRecurring = stats?.monthlyRecurring;

  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ml-1 shrink-0 md:hidden"
            onClick={onBack}
            aria-label="Back to membership plans"
          >
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="break-words text-2xl font-semibold tracking-normal">{plan.name}</h1>
              <StatusChip status={plan.status} className="rounded-md" />
              {plan.priority > 0 ? <Badge variant="outline">Priority {plan.priority}</Badge> : null}
            </div>
            {plan.description ? (
              <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col items-start gap-2 lg:items-end">{actions}</div>
      </div>

      <div className="mt-5 border-t pt-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div className="grid gap-3 sm:grid-cols-3">
            <SummaryMetric label="Active members" value={activeMembers ?? '—'} />
            <SummaryMetric
              label="Monthly recurring"
              value={
                monthlyRecurring != null ? formatMoney(monthlyRecurring, plan.priceCurrency) : '—'
              }
            />
            <SummaryMetric label="Contract" value={formatContractTerm(plan.contractLengthMonths)} />
          </div>

          <div className="shrink-0 lg:text-right">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Price
            </p>
            <p className="mt-1 text-3xl font-semibold tabular-nums">
              {formatMoney(billingAmount, plan.priceCurrency)}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              per {BILLING_FREQUENCY_PERIOD[frequency]}
            </p>
            {freeOptions.length > 0 ? (
              // Only the office sees or picks these; the price above is what
              // the plan is offered at.
              <p className="mt-1 text-xs text-muted-foreground">
                + {freeOptions.map((option) => option.label).join(', ')} · free, office only
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function SummaryMetric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function MembershipPlanSummaryActions({
  plan,
  memberCount,
  onDuplicate,
  onArchive,
  onRestore,
  isDuplicating,
  isArchiving,
  isRestoring,
}: {
  plan: MembershipPlan;
  memberCount?: number;
  onDuplicate: () => void;
  onArchive: () => void;
  onRestore: () => void;
  isDuplicating: boolean;
  isArchiving: boolean;
  isRestoring: boolean;
}) {
  const router = useRouter();
  const [showConfirm, setShowConfirm] = useState(false);

  const handleEdit = () => {
    void router.navigate({
      to: '/dashboard/membership-plans/$id/edit',
      params: { id: planId(plan) },
      search: (prev) => prev as never,
    });
  };

  const handleArchive = () => {
    if (plan.status === 'ARCHIVED') {
      onRestore();
      return;
    }
    setShowConfirm(true);
  };

  const confirmArchive = () => {
    setShowConfirm(false);
    onArchive();
  };

  const deleteLabel = plan.status === 'ARCHIVED' ? 'Restore' : 'Delete';

  return (
    <>
      <div className="hidden items-center gap-1.5 md:flex">
        <Button variant="outline" size="icon-sm" title="Edit" onClick={handleEdit}>
          <Pencil size={14} />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          title="Duplicate"
          onClick={onDuplicate}
          disabled={isDuplicating}
        >
          {isDuplicating ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          title={plan.status === 'ARCHIVED' ? 'Unarchive' : 'Archive'}
          className={cn(
            plan.status === 'ARCHIVED'
              ? 'text-emerald-700 border-emerald-600/40 hover:bg-emerald-600/10'
              : 'text-destructive border-destructive/40 hover:bg-destructive/10',
          )}
          onClick={handleArchive}
          disabled={isArchiving || isRestoring}
        >
          {plan.status === 'ARCHIVED' ? <ArchiveRestore size={14} /> : <Archive size={14} />}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon-sm" title="Actions" aria-label="Actions">
              <MoreVertical size={14} />
            </Button>
          </DropdownMenuTrigger>
          <OverflowMenuContent>
            <DropdownMenuItem onSelect={handleEdit}>
              <Pencil className="mr-2 size-3.5" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onDuplicate} disabled={isDuplicating}>
              {isDuplicating ? (
                <Loader2 className="mr-2 size-3.5 animate-spin" />
              ) : (
                <Copy className="mr-2 size-3.5" />
              )}
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                if (plan.status === 'ARCHIVED') {
                  onRestore();
                  return;
                }
                setShowConfirm(true);
              }}
              className={
                plan.status === 'ARCHIVED'
                  ? 'text-emerald-700 focus:text-emerald-700'
                  : 'text-destructive focus:text-destructive'
              }
            >
              {plan.status === 'ARCHIVED' ? (
                <ArchiveRestore className="mr-2 size-3.5" />
              ) : (
                <Trash2 className="mr-2 size-3.5" />
              )}
              {deleteLabel}
            </DropdownMenuItem>
          </OverflowMenuContent>
        </DropdownMenu>
      </div>

      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon-sm" title="Actions" aria-label="Actions">
              <MoreVertical size={14} />
            </Button>
          </DropdownMenuTrigger>
          <OverflowMenuContent>
            <DropdownMenuItem onSelect={handleEdit}>
              <Pencil className="mr-2 size-3.5" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onDuplicate} disabled={isDuplicating}>
              {isDuplicating ? (
                <Loader2 className="mr-2 size-3.5 animate-spin" />
              ) : (
                <Copy className="mr-2 size-3.5" />
              )}
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                if (plan.status === 'ARCHIVED') {
                  onRestore();
                  return;
                }
                setShowConfirm(true);
              }}
              className={
                plan.status === 'ARCHIVED'
                  ? 'text-emerald-700 focus:text-emerald-700'
                  : 'text-destructive focus:text-destructive'
              }
            >
              {plan.status === 'ARCHIVED' ? (
                <ArchiveRestore className="mr-2 size-3.5" />
              ) : (
                <Trash2 className="mr-2 size-3.5" />
              )}
              {deleteLabel}
            </DropdownMenuItem>
          </OverflowMenuContent>
        </DropdownMenu>
      </div>

      <ArchivePlanDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={confirmArchive}
        name={plan.name}
        memberCount={memberCount}
        isPending={isArchiving}
      />
    </>
  );
}

function ArchivePlanDialog({
  open,
  onOpenChange,
  onConfirm,
  name,
  memberCount,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: () => void;
  name: string;
  memberCount?: number;
  isPending: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive plan?</AlertDialogTitle>
          <AlertDialogDescription>
            Archive "{name}"? Existing members keep their benefits but no new members can join.
            {memberCount && memberCount > 0 ? (
              <>
                {' '}
                This plan currently has {memberCount} active member
                {memberCount === 1 ? '' : 's'}.
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? (
              <Loader2 className="mr-1 size-3.5 animate-spin" />
            ) : (
              <Trash2 className="mr-1 size-3.5" />
            )}
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MemberPricingContent({ plan }: { plan: MembershipPlan }) {
  const hasCustomRates = membershipPlanHasCustomRates(plan);
  const applicableTypes = membershipPlanApplicableTypes(plan);

  if (hasCustomRates) {
    return (
      <div className="space-y-3">
        <ol className="space-y-2">
          {(plan.discountTiers ?? []).map((tier, index) => (
            <li key={`${tier.label}-${index}`} className="rounded-lg border bg-muted/30 p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {index + 1}. {tier.label}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(tier.appliesTo.length > 0 ? tier.appliesTo : ['everything']).map((type) => (
                      <Badge key={type} variant="secondary" className="text-[11px]">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
                <p className="shrink-0 text-sm font-medium text-foreground">
                  {formatTierSummary(tier.type, tier.value, plan.priceCurrency)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground">
        Members get <span className="font-semibold">{formatMembershipPlanDiscount(plan)} off</span>{' '}
        on:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {(applicableTypes.length > 0 ? applicableTypes : ['everything']).map((type) => (
          <Badge key={type} variant="secondary" className="text-[11px] capitalize">
            {type[0] + type.slice(1).toLowerCase()}
          </Badge>
        ))}
      </div>
      {(plan.eligibleLineItemIds?.length ?? 0) > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Only:</p>
          <ItemNameChips ids={plan.eligibleLineItemIds} />
        </div>
      ) : null}
      {(plan.excludedLineItemIds?.length ?? 0) > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Never:</p>
          <ItemNameChips ids={plan.excludedLineItemIds} />
        </div>
      ) : null}
    </div>
  );
}

// Read-only name chips for an id list (resolves names via the pricebook query,
// per the house rule: show names, never raw ids).
function ItemNameChips({ ids }: { ids: string[] }) {
  const { data } = usePricebookItems({ ids }, { enabled: ids.length > 0, showErrorToast: false });
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of data?.items ?? []) map.set(item.id, item.name);
    return map;
  }, [data]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <Badge key={id} variant="secondary" className="text-[11px]">
          {nameById.get(id) ?? '…'}
        </Badge>
      ))}
    </div>
  );
}

function formatEquipmentCategory(value: string): string {
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bAc\b/g, 'AC')
    .replace(/\bHvac\b/g, 'HVAC');
}

function formatRecurringCadence(service: PlanRecurringService): string {
  if (service.recurrenceType === 'SEASONAL') {
    return `${service.recurrenceMonths.length} seasonal visits / year`;
  }
  const interval = Math.max(1, service.recurrenceInterval);
  const unit = service.recurrenceType === 'MONTHLY' ? 'month' : 'year';
  return `Every ${interval} ${unit}${interval === 1 ? '' : 's'}`;
}

function formatRecurringScope(service: PlanRecurringService): string {
  if (service.attachTo !== 'UNIT') return 'One visit per property';
  return service.scaling === 'MULTIPLY'
    ? 'One visit per covered unit'
    : 'One visit · extended for covered units';
}

function IncludedContent({
  plan,
  recurringServices,
}: {
  plan: MembershipPlan;
  recurringServices: PlanRecurringService[];
}) {
  const coveredServices = plan.coveredServices ?? [];
  const waivers = plan.waivers ?? [];
  const coverageRules = plan.coverageRules ?? [];

  if (
    coveredServices.length === 0 &&
    waivers.length === 0 &&
    coverageRules.length === 0 &&
    recurringServices.length === 0
  ) {
    return <p className="text-sm text-muted-foreground">No covered services.</p>;
  }

  return (
    <div className="space-y-4">
      {coverageRules.length > 0 ? (
        <div className="space-y-2">
          {coverageRules.map((coverage) => (
            <div key={coverage.id} className="rounded-lg border bg-muted/30 px-3 py-2">
              <p className="text-sm font-medium">
                {formatEquipmentCategory(coverage.equipmentCategory)} coverage
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="text-[11px]">
                  {coverage.includedQuantity} unit
                  {coverage.includedQuantity === 1 ? '' : 's'} included
                </Badge>
                <Badge variant="secondary" className="text-[11px]">
                  {formatMoney(extraUnitPriceOf(coverage).extraUnitPrice, plan.priceCurrency)} /
                  extra unit /{' '}
                  {extraUnitPriceOf(coverage).extraUnitPricePer === 'YEAR' ? 'year' : 'month'}
                </Badge>
                {coverage.isPrimary ? (
                  <Badge variant="secondary" className="text-[11px]">
                    Primary
                  </Badge>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {recurringServices.length > 0 ? (
        <div className="space-y-2">
          {coverageRules.length > 0 ? <div className="border-t pt-4" /> : null}
          {recurringServices.map((service) => (
            <div key={service.id} className="rounded-lg border bg-muted/30 px-3 py-2">
              <p className="text-sm font-medium">{service.label || 'Recurring visit'}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="text-[11px]">
                  {formatRecurringCadence(service)}
                </Badge>
                <Badge variant="secondary" className="text-[11px]">
                  {formatRecurringScope(service)}
                </Badge>
                {service.extraUnitMinutes > 0 ? (
                  <Badge variant="secondary" className="text-[11px]">
                    +{service.extraUnitMinutes} min / extra unit
                  </Badge>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {coveredServices.length > 0 ? (
        <div className="space-y-2">
          {coverageRules.length > 0 || recurringServices.length > 0 ? (
            <div className="border-t pt-4" />
          ) : null}
          {coveredServices.map((service, index) => (
            <div
              key={`${service.name}-${index}`}
              className="rounded-lg border bg-muted/30 px-3 py-2"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{service.name}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge variant="secondary" className="text-[11px]">
                      {service.quantityPerPeriod} /{' '}
                      {COVERED_SERVICE_PERIOD_LABELS[service.entitlementPeriodType]}
                    </Badge>
                    {service.rolloverPolicy !== 'EXPIRE' ? (
                      <Badge variant="secondary" className="text-[11px]">
                        {service.rolloverPolicy === 'ROLLOVER_LIMITED' &&
                        Number(service.rolloverMax) > 0
                          ? `Rolls over up to ${Number(service.rolloverMax)}`
                          : 'Rolls over'}
                      </Badge>
                    ) : null}
                    {service.seasonalWindow ? (
                      <Badge variant="secondary" className="text-[11px]">
                        {formatSeasonalWindow(service.seasonalWindow)}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {waivers.length > 0 ? (
        <div className="space-y-2">
          {coveredServices.length > 0 ? <div className="border-t pt-4" /> : null}
          {waivers.map((waiver, index) => (
            <div
              key={`${waiver.name}-${index}`}
              className="rounded-lg border bg-muted/30 px-3 py-2"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{waiver.name}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge variant="secondary" className="text-[11px]">
                      {WAIVER_TYPE_LABELS[waiver.waiverType]}
                    </Badge>
                    <Badge variant="secondary" className="text-[11px]">
                      {waiver.maxPerPeriod != null
                        ? `${waiver.maxPerPeriod} / billing cycle · legacy`
                        : 'Unlimited while active'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ContractRenewalContent({ plan }: { plan: MembershipPlan }) {
  const cancellationRules = plan.cancellationRules;
  const renewalRules = plan.renewalRules;
  const freeMonthOffer = plan.saveOfferTemplates?.find((offer) => offer.type === 'FREE_MONTH');
  const creditOffer = plan.saveOfferTemplates?.find((offer) => offer.type === 'CREDIT');
  const retentionOffers = [
    freeMonthOffer?.label,
    creditOffer
      ? `${formatMoney(
          Math.round(Math.abs(creditOffer.value ?? 0) * 100) / 100,
          plan.priceCurrency,
        )} credit`
      : null,
  ].filter((value): value is string => Boolean(value));

  return (
    <DetailFieldGrid>
      <DetailField label="Contract" value={formatContractTerm(plan.contractLengthMonths)} />
      <DetailField
        label="Auto-renew"
        value={
          renewalRules ? (renewalRules.autoRenewDefault ? 'On by default' : 'Off by default') : '—'
        }
      />
      <DetailField
        label="Renewal reminder"
        value={
          renewalRules && renewalRules.renewalReminderDays > 0
            ? `${renewalRules.renewalReminderDays} days before`
            : '—'
        }
      />
      <DetailField
        label="Cancellation"
        value={cancellationRules ? formatCancellationMode(cancellationRules.defaultMode) : '—'}
      />
      <DetailField
        label="Early termination fee"
        value={
          cancellationRules?.etfEnabled
            ? cancellationRules.etfType === 'FLAT'
              ? `${formatMoney(cancellationRules.etfValue ?? 0, plan.priceCurrency)} flat`
              : `${cancellationRules.etfValue ?? 0}% of remaining`
            : 'None'
        }
      />
      <DetailField
        label="Retention offer"
        value={retentionOffers.length > 0 ? retentionOffers.join(' · ') : 'None'}
      />
    </DetailFieldGrid>
  );
}

function BenefitsContent({ valuePoints }: { valuePoints: string[] }) {
  return (
    <ul className="space-y-2">
      {valuePoints.map((point, index) => (
        <li
          key={`${point}-${index}`}
          className="flex items-start gap-2 rounded-lg border px-3 py-2"
        >
          <Check className="mt-0.5 size-3.5 shrink-0 text-success" />
          <span className="text-sm">{point}</span>
        </li>
      ))}
    </ul>
  );
}

function InternalCard({
  plan,
  onSyncStripe,
  syncingStripe,
}: {
  plan: MembershipPlan;
  onSyncStripe: () => void;
  syncingStripe: boolean;
}) {
  const [open, setOpen] = useState(false);
  const companyTimezone = useCompanyTimezone();
  // `billingProvider` is the signal a plan is wired to Stripe; without it the
  // five sync rows are all "—", so hide them and keep just the support IDs.
  const hasProvider = Boolean(plan.billingProvider);

  return (
    <section className="rounded-xl border bg-card px-4 py-4 text-sm text-card-foreground shadow-sm">
      <CollapsibleSectionHeader
        icon={Lock}
        label="Internal"
        open={open}
        onToggle={() => setOpen((value) => !value)}
      />
      {open ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {plan.providerPriceId
                ? 'Stripe billing is ready.'
                : 'Sync before using automatic membership charges.'}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onSyncStripe}
              disabled={syncingStripe}
            >
              {syncingStripe ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 size-3.5" />
              )}
              Sync Stripe
            </Button>
          </div>
          <DetailFieldGrid>
            <DetailField label="Version" value={`v${plan.planVersion}`} />
            <DetailField
              label="Tenant"
              value={plan.tenantId}
              valueClassName="text-xs font-normal text-muted-foreground"
            />
            <DetailField
              label="Plan ID"
              value={planId(plan)}
              valueClassName="text-xs font-normal text-muted-foreground"
            />
            {hasProvider ? (
              <>
                <DetailField label="Provider" value={plan.billingProvider?.toUpperCase() ?? '—'} />
                <DetailField label="Product ID" value={plan.providerProductId ?? '—'} />
                <DetailField label="Price ID" value={plan.providerPriceId ?? '—'} />
                <DetailField
                  label="Last synced"
                  value={
                    plan.providerSyncedAt
                      ? formatDateTimeInTimeZone(plan.providerSyncedAt, companyTimezone, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : '—'
                  }
                />
                <DetailField label="Sync error" value={plan.providerSyncError ?? '—'} />
              </>
            ) : null}
          </DetailFieldGrid>
        </div>
      ) : null}
    </section>
  );
}

function formatMoney(amount: number, currency?: string | null): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency ?? 'USD',
  }).format(amount ?? 0);
}

function formatContractTerm(contractLengthMonths?: number | null): string {
  if (contractLengthMonths == null) return 'Month-to-month';
  return `${contractLengthMonths} month${contractLengthMonths === 1 ? '' : 's'}`;
}

function formatCancellationMode(mode: string): string {
  return mode === 'IMMEDIATE' ? 'Immediately' : 'At period end';
}

function formatTierSummary(type: string, value: number, currency?: string | null): string {
  if (type === 'FLAT') return `${formatMoney(value, currency)} off`;
  if (type === 'SET_PRICE') return `Set price ${formatMoney(value, currency)}`;
  return `${value}% off`;
}
